<script setup>

import {datePipe} from "@/utils/datatimeUtils";
import {computed, getCurrentInstance, onMounted, reactive, ref} from "vue";
import {useRouter} from "vue-router";
import {accessControlApi, articleListsApi} from "@/api/forwriting_index";
import LoadingIcon    from "@/components/LoadingIcon.vue";
import CheckPointLimit from "@/views/forwriting/fw00_check_point_limit.vue"

const instance = getCurrentInstance()
const router = useRouter();
const showDeleteModal = ref(false);
const isLoading = ref(false);

const id_for_delete = ref(-1);
const data = reactive({
  items: []
})

const filterKey = reactive({
  searchKey: null,
});
const searchKey = ref("");

const filteredData = computed(() => {
  let temp = data.items || [];
  if (filterKey.searchKey) {
    temp = temp.filter((row) =>
        Object.keys(row).some(
            (key) =>
                String(row[key]).toLowerCase().indexOf(filterKey.searchKey.toLowerCase()) > -1
        )
    );
  }
  return temp;
});

const items_counter = computed(() => {
  return filteredData.value.length;
});

onMounted(async () => {
  if( ! await accessControlApi.check_option_access()) {
    await router.push(`/forwriting/options_error`)
    return
  }

  const resp = await articleListsApi.getAll();
  data.items = resp.data;
  console.log( "*** data.items:", data.items)

  // check the point_limit
  point_limit.is_over = await ref_CheckPointLimit.value.check()
});

const handleFilter = () => {
  filterKey.searchKey = searchKey.value;
};

const handleCreate = () => {
  router.push(`/forwriting/article_lists/new`);
};

const delete_modal_msg = ref( "")

const handleDelete = async ( par_id, article_list_name) => {
  console.log( "*** par_id:", par_id)
  console.log( "*** article_list_name:", article_list_name)

  let t = instance.proxy.$t
  delete_modal_msg.value = t('ArticleLists.delete_prompt'
    ).replace( "name", article_list_name)

  id_for_delete.value = par_id
  showDeleteModal.value = true;
};

const handleDelete_confirm = async () => {
  isLoading.value = true;

  try {
    const resp = await articleListsApi.delete( id_for_delete.value );
    if( resp.status !== 200) {
      isLoading.value = false;
      throw new Error("ERROR");
    }

    data.items = data.items.filter( ( item) => item.id !== id_for_delete.value);

    isLoading.value = false;
    showDeleteModal.value = false;
  } catch (error) {
    isLoading.value = false;
    console.error("刪除文章列表失敗:", error);
    console.error("錯誤詳情:", {
      message: error.message,
      response: error.response,
      config: error.config
    });
    
    // 提取後端返回的友好錯誤消息（支持日文|中文格式）
    let errorMessage = 'Network Error';
    if (error.response && error.response.data) {
      if (error.response.data.detail) {
        const detail = error.response.data.detail;
        // 檢查是否為「日文|中文」格式
        if (detail.includes('|')) {
          const [jaMsg, twMsg] = detail.split('|');
          // 根據當前語言選擇對應的訊息
          const currentLocale = instance.proxy.$i18n.locale.value || 'ja';
          errorMessage = currentLocale === 'tw' ? twMsg.trim() : jaMsg.trim();
        } else {
          errorMessage = detail;
        }
      } else if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    alert(`刪除失敗: ${errorMessage}`);
  }
};

const handleEdit_detail = (article_list_id) => {
  console.log( "article_list_id:", article_list_id)
  router.push(`/forwriting/article_lists/${article_list_id}/articles`);
};

// ==== modal_over_points ====
const ref_CheckPointLimit = ref(null)
const point_limit = reactive({
  is_over: false,
})

</script>

<template>
  <LoadingIcon :is-loading="isLoading"></LoadingIcon>

  <!-- modal_over_points -->
  <CheckPointLimit ref="ref_CheckPointLimit"></CheckPointLimit>

  <!-- Delete_modal -->
  <div v-if="showDeleteModal" class="modal-del">
    <div class="modal-content">

      <div class="row" style="margin-top: 20px;text-align: center">
        <p>{{ delete_modal_msg }}</p>
<!--        <p>{{ $t( "ArticleLists.delete_prompt") }}</p>-->
      </div>

      <!-- 2 buttons -->
      <div class="row" style="margin-top: 20px;margin-left: 10px; margin-right: 10px">
        <button class="btn btn-primary"
                @click="handleDelete_confirm">
          {{ $t( "ArticleLists.OK") }}</button>
      </div>

      <div class="row" style="margin-top: 20px;margin-left: 10px; margin-right: 10px">
        <button class="btn btn-primary"
                @click="showDeleteModal = false">
          {{ $t( "ArticleLists.cancel") }}</button>
      </div>

    </div>
  </div>
  <!-- end of Delete_modal -->

  <div class="container">

    <!-- title-->
    <div class="row">
      <h4>{{$t( "ArticleLists.h4")}}</h4>
    </div>

    <!-- filter-->
    <div class="row">
      <div class="column">

        <label for="search-keyword">{{ $t('ArticleLists.search_keyword') }}: </label>
        <input type="text" id="search-keyword" style="margin-left: 5px"
               v-model="searchKey" />

        <button class="btn btn-primary" @click="handleFilter"
                style="padding: 0 50px; margin: 10px;">
          {{ $t( "ArticleLists.filter") }}</button>

      </div>

    </div>

    <!-- counter and new button -->
    <div class="row">

      <div class="column">
        <label for="list-count">{{ $t('ArticleLists.count').replace(/[:：]\s*$/, '') }}:</label>
        <span id="list-count" style="margin-left: 5px">{{ items_counter}}</span>
      </div>

      <div class="column" style="text-align: right">
        <button class="btn btn-primary" style="padding: 0 50px; margin: 10px;"
        @click="handleCreate">
          {{ $t('ArticleLists.create') }}</button>
      </div>

    </div>

    <!-- リスト一覧テーブル(清單清單表) -->
    <table class="table table-hover">

      <thead>
        <tr>
          <th scope="col">{{ $t('ArticleLists.list_title') }}</th>
          <th scope="col">{{ $t('ArticleLists.author') }}</th>
          <th></th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="item in filteredData" :key="item.id" >
          <td style="cursor: pointer" @click="handleEdit_detail(item.id)">
            {{ item.article_list_name }}</td>
          <td>{{ datePipe(item.created_at) }}</td>

          <td style="text-align: right">
            <button class="btn btn-danger btn-sm"
                    @click="handleDelete( item.id, item.article_list_name)">
              {{ $t('ArticleLists.delete') }}
            </button>
          </td>
        </tr>
      </tbody>

    </table>

  </div>

</template>

<style scoped>

  .modal-del {
    height: 800px;
    max-height: 1024px;
    width: 600px;

    display: flex;
    justify-content: center;
    align-items: center;

    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0);
    z-index: 1000; /* 他の要素より前面に */

    top: 100px;
    left: 100px;
    position: absolute;
    padding: 20px;
  }

  .modal-content {
    background-color: #fff;
    padding: 20px;
    border-radius: 5px;
    max-width: 1600px;  /* お好みに合わせて変更 */
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }

</style>